import { useState, useCallback, useRef, useEffect } from "react";
import GoogleMapReact from "google-map-react";
import MapService from "services/map.service";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { mapAction } from "redux/slices/map/mapSlice";
import { Box, IconButton, Tooltip } from "@mui/material";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import EditMapInputForm from "../EditMapInputs/EditMapInputForm";
import MapCenterPin from "./MapCenterPin";

const MapWithPin = () => {
  const form = "AddBookingForm";

  const dispatch = useAppDispatch();

  const [mapApi, setMapApi] = useState<any>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [pinBounce, setPinBounce] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const picklatlong = useAppSelector(
    (state) => state.form?.[form]?.values?.pickup?.details?.geometry?.location
  );
  const destinationlatlong = useAppSelector(
    (state) =>
      state.form?.[form]?.values?.destination?.details?.geometry?.location
  );
  const startMapformatted = useAppSelector(
    (state) => state.map?.startMapformatted
  );
  const endMapformatted = useAppSelector((state) => state.map?.endMapformatted);

  const endLat = useAppSelector((state) => state.map.endLat);
  const endlng = useAppSelector((state) => state.map.endlng);

  const show = useAppSelector((state) => state.map.show);
  const isPickup = show === 0;

  const getDefaultCenter = useCallback(() => {
    if (isPickup) {
      return {
        lat: picklatlong?.lat ?? 24.7191924,
        lng: picklatlong?.lng ?? 46.6722224,
      };
    }
    return {
      lat: destinationlatlong?.lat ?? endLat,
      lng: destinationlatlong?.lng ?? endlng,
    };
  }, [isPickup, picklatlong, destinationlatlong, endLat, endlng]);

  const fetchAddressAndDispatch = useCallback(
    async (map: any) => {
      setIsGeocoding(true);
      try {
        const center = map.getCenter();
        const newAddress: any = await MapService.getAddressFromLatLng(
          center.lat(),
          center.lng()
        );

        let airport = false;
        let city: any, route: any, country: any, coordinates: any;
        const location = newAddress.geometry.location;
        const geometry = {
          location: { lat: location.lat(), lng: location.lng() },
        };
        coordinates = { lat: location.lat(), lng: location.lng() };

        newAddress.address_components.every(
          ({ types, long_name, short_name }: any) => {
            if (types.includes("route")) route = long_name;
            if (types.includes("locality")) city = long_name;
            if (types.includes("country")) country = short_name;
            if (route && city && country) return false;
            return true;
          }
        );

        if (newAddress.types.includes("airport")) airport = true;

        const startTitle = newAddress?.formatted_address
          ?.split(/[،,]+/)
          .slice(0, 5)
          .join(",");

        const details = {
          ...newAddress,
          address: startTitle,
          city,
          route,
          name: startTitle,
          title: startTitle,
          airport,
          geometry,
          country_name_code: country,
          coordinates,
        };

        const mainAddress = { value: newAddress.formatted_address, details };

        if (show === 0) {
          dispatch(mapAction.setStartMapFormatted(mainAddress));
        } else {
          dispatch(mapAction.setdestinationMap(false));
          dispatch(mapAction.setEndMapFormatted(mainAddress));
        }
      } finally {
        setIsGeocoding(false);
      }
    },
    [dispatch, show]
  );

  const handleApiLoaded = useCallback(
    (map: any) => {
      setMapApi(map);
      fetchAddressAndDispatch(map);
    },
    [fetchAddressAndDispatch]
  );

  const triggerGeocode = useCallback(
    (withBounce = false) => {
      if (!mapApi) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);

      debounceRef.current = setTimeout(async () => {
        if (withBounce) setPinBounce(true);
        await fetchAddressAndDispatch(mapApi);
        if (withBounce) {
          setTimeout(() => setPinBounce(false), 350);
        }
      }, 300);
    },
    [mapApi, fetchAddressAndDispatch]
  );

  const handleDrag = useCallback(() => {
    triggerGeocode(true);
  }, [triggerGeocode]);

  const handleRecenter = useCallback(() => {
    if (!mapApi) return;

    const mapFormatted = isPickup ? startMapformatted : endMapformatted;
    const formattedCoords = mapFormatted?.details?.geometry?.location;
    const formCoords = isPickup ? picklatlong : destinationlatlong;
    const center = formattedCoords ?? formCoords ?? getDefaultCenter();

    mapApi.panTo(center);
    triggerGeocode(false);
  }, [
    mapApi,
    isPickup,
    startMapformatted,
    endMapformatted,
    picklatlong,
    destinationlatlong,
    getDefaultCenter,
    triggerGeocode,
  ]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const defaultCenter = getDefaultCenter();

  return (
    <Box sx={{ width: "100%" }}>
      <EditMapInputForm isGeocoding={isGeocoding} />
      <Box
        sx={{
          mt: 2,
          height: { xs: "min(52vh, 480px)", sm: "min(52vh, 520px)" },
          width: "100%",
          position: "relative",
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.12)",
        }}
      >
        <MapCenterPin bounce={pinBounce} />

        <Tooltip title="Recenter map">
          <IconButton
            onClick={handleRecenter}
            aria-label="Recenter map"
            sx={{
              position: "absolute",
              bottom: 16,
              right: 16,
              zIndex: 100,
              bgcolor: "background.paper",
              boxShadow: 2,
              "&:hover": { bgcolor: "background.paper" },
            }}
            size="small"
          >
            <MyLocationIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <GoogleMapReact
          defaultCenter={defaultCenter}
          defaultZoom={13}
          onGoogleApiLoaded={({ map }) => handleApiLoaded(map)}
          onDragEnd={handleDrag}
          onZoomAnimationEnd={handleDrag}
        />
      </Box>
    </Box>
  );
};

export default MapWithPin;
